import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CartProvider } from "./contexts/CartContext";
import ThemeBackground from "./components/ThemeBackground";
import TelegramSupportWidget from "./components/TelegramSupportWidget";
import TelegramAccountModal from "./components/TelegramAccountModal";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SiteCatalog from "./pages/SiteCatalog";
import OrderPage from "./pages/OrderPage";
import CreditPackages from "./pages/CreditPackages";
import ApiAccessPage from "./pages/ApiAccessPage";
import ApiBalancePage from "./pages/ApiBalancePage";
import SEOServices from "./pages/SEOServices";
import MaintenancePage from "./pages/MaintenancePage";
import MyLinks from "./pages/MyLinks";
import SupportPage from "./pages/SupportPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import CryptoPayment from "./pages/CryptoPayment";
import "./App.css";

// Global loading screen component
const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <div className="text-primary font-mono">Yükleniyor...</div>
    </div>
  </div>
);

function TelegramSupportWidgetPlacement() {
  const location = useLocation();
  const hideOnSupport = location.pathname === "/support" || location.pathname.startsWith("/support/");
  return !hideOnSupport ? <TelegramSupportWidget /> : null;
}

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, maintenanceMode } = useAuth();
  
  // Show nothing during initial auth check - parent handles loading
  if (loading) {
    return null;
  }
  
  if (maintenanceMode && !["admin", "super_admin"].includes(user?.role)) {
    return <MaintenancePage />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !["admin", "super_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user, loading, maintenanceMode } = useAuth();

  // Show loading screen while checking auth - prevents flash
  if (loading) {
    return <LoadingScreen />;
  }

  const blockNonAdminMaintenance =
    maintenanceMode && (!user || !["admin", "super_admin"].includes(user.role));

  return (
    <Routes>
      <Route
        path="/"
        element={
          user ? (
            <Navigate to="/dashboard" replace />
          ) : blockNonAdminMaintenance ? (
            <MaintenancePage />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route
        path="/register"
        element={
          user ? (
            <Navigate to="/dashboard" replace />
          ) : blockNonAdminMaintenance ? (
            <MaintenancePage />
          ) : (
            <RegisterPage />
          )
        }
      />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {["admin", "super_admin"].includes(user?.role) ? <AdminDashboard /> : <CustomerDashboard />}
        </ProtectedRoute>
      } />
      
      <Route path="/sites" element={
        <ProtectedRoute>
          <SiteCatalog />
        </ProtectedRoute>
      } />
      
      <Route path="/my-links" element={
        <ProtectedRoute>
          <MyLinks />
        </ProtectedRoute>
      } />
      
      <Route path="/order/:siteId" element={
        <ProtectedRoute>
          <OrderPage />
        </ProtectedRoute>
      } />
      
      <Route path="/credits" element={
        <ProtectedRoute>
          <CreditPackages />
        </ProtectedRoute>
      } />

      <Route path="/api-balance" element={
        <ProtectedRoute>
          <ApiBalancePage />
        </ProtectedRoute>
      } />

      <Route path="/api-access" element={
        <ProtectedRoute>
          <ApiAccessPage />
        </ProtectedRoute>
      } />
      
      <Route path="/seo-services" element={
        <ProtectedRoute>
          <SEOServices />
        </ProtectedRoute>
      } />
      
      <Route path="/support" element={
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/*" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/payment/crypto/:paymentId" element={<CryptoPayment />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <CartProvider>
              <ThemeBackground />
              <AppRoutes />
              <TelegramAccountModal />
              <TelegramSupportWidgetPlacement />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
